/**
 * Roles tab — the 5 role checkboxes plus the role-gated prescribing-proxy
 * sub-block (GDN-1119). Co-signing lives in the Supervision tab.
 */

import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';

interface RolesSectionProps {
  rolesToDisplay: ProviderRoleInfo[];
  selectedRoles: Set<string>;
  onToggleRole: (role: string) => void;
  canPrepareOnBehalf: boolean;
  onCanPrepareOnBehalfChange: (value: boolean) => void;
  disabled?: boolean;
}

export function RolesSection({
  rolesToDisplay,
  selectedRoles,
  onToggleRole,
  canPrepareOnBehalf,
  onCanPrepareOnBehalfChange,
  disabled = false,
}: RolesSectionProps) {
  const isOperationsSupport = selectedRoles.has(ROLES.OPERATIONS_SUPPORT);

  return (
    <Stack spacing={2}>
      <FormGroup>
        {rolesToDisplay.map(role => (
          <FormControlLabel
            key={role.role}
            control={
              <Checkbox
                checked={selectedRoles.has(role.role)}
                onChange={() => onToggleRole(role.role)}
                disabled={disabled}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {role.display_name || getRoleDisplayName(role.role)}
                </Typography>
                {role.description && (
                  <Typography variant="caption" color="text.secondary">
                    {role.description}
                  </Typography>
                )}
              </Box>
            }
            sx={{ alignItems: 'flex-start', py: 0.5 }}
          />
        ))}
      </FormGroup>

      {/* GDN-1119: per-person prescribing-proxy capability, ops-support only */}
      {isOperationsSupport && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Prescribing
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={canPrepareOnBehalf}
                  onChange={e => onCanPrepareOnBehalfChange(e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Prescribing Proxy</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Prep prescriptions on behalf of a prescriber. Requires JotRx on the account.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </>
      )}

    </Stack>
  );
}
